import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Result,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  SafetyCertificateOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { useAuthStore } from '@/store/authStore';
import workshopService, { type WorkshopJobRow } from '@/services/workshop.service';
import {
  HOLD_TAG_COLOR,
  holdLabel,
  nextActionForJob,
  STAGE_TAG_COLOR,
  stageLabel,
} from './workshopLabels';

const { Text } = Typography;

type AttentionKind = 'hold' | 'qc' | 'release' | 'vendor' | 'no_execution';

function attentionRank(row: WorkshopJobRow): number {
  if (row.cost_responsibility === 'VENDOR' && !row.execution_id) return 0;
  if (row.execution_stage === 'READY_FOR_RELEASE') return 1;
  if (row.execution_stage === 'QC_PENDING' || row.qc_state === 'FAILED') return 2;
  if (row.hold_reason === 'WAITING_PARTS') return 3;
  if (row.hold_reason) return 4;
  if (!row.execution_id && row.case_status !== 'completed') return 5;
  return 99;
}

const WorkshopOperationsPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canView = Boolean(
    user?.is_super_admin ||
      user?.permissions?.includes('workshop.view') ||
      user?.permissions?.includes('asset.maintenance.view') ||
      user?.permissions?.includes('asset.view')
  );

  const [stageFilter, setStageFilter] = useState<string | undefined>();
  const [holdFilter, setHoldFilter] = useState<string | undefined>();
  const [q, setQ] = useState('');

  const { data: jobs = [], isLoading, isError, error, refetch } = useQuery({
    queryKey: ['workshop', 'jobs', stageFilter, holdFilter],
    queryFn: () =>
      workshopService.listJobs({
        stage: stageFilter,
        hold_reason: holdFilter,
      }),
    enabled: canView,
    refetchInterval: 60_000,
  });

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let rows = [...jobs];
    if (term) {
      rows = rows.filter(
        (r) =>
          r.case_number?.toLowerCase().includes(term) ||
          r.title?.toLowerCase().includes(term) ||
          r.asset_code?.toLowerCase().includes(term) ||
          r.asset_name?.toLowerCase().includes(term)
      );
    }
    return rows.sort((a, b) => attentionRank(a) - attentionRank(b));
  }, [jobs, q]);

  const summary = useMemo(() => {
    const active = jobs.filter((j) => j.execution_id).length;
    const onHold = jobs.filter((j) => j.hold_reason).length;
    const qcPending = jobs.filter((j) => j.execution_stage === 'QC_PENDING').length;
    const ready = jobs.filter((j) => j.execution_stage === 'READY_FOR_RELEASE').length;
    const vendorBlocked = jobs.filter(
      (j) => j.cost_responsibility === 'VENDOR' && !j.execution_id
    ).length;
    return { active, onHold, qcPending, ready, vendorBlocked, total: jobs.length };
  }, [jobs]);

  const attention = useMemo(
    () => filtered.filter((j) => attentionRank(j) < 90).slice(0, 8),
    [filtered]
  );

  if (!canView) {
    return (
      <Result
        status="403"
        title="Permission required"
        subTitle="You need workshop or maintenance view permission to open Workshop Operations."
      />
    );
  }

  const columns: ColumnsType<WorkshopJobRow> = [
    {
      title: 'Equipment',
      key: 'equipment',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.asset_code || r.asset_name || 'Asset'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.asset_name && r.asset_code ? r.asset_name : r.asset_id?.slice(0, 8)}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Work order',
      dataIndex: 'case_number',
      key: 'case_number',
      render: (v: string, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v}</Text>
          <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
            {r.title}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Stage',
      key: 'stage',
      width: 150,
      render: (_, r) =>
        r.execution_stage ? (
          <Tag color={STAGE_TAG_COLOR[r.execution_stage] || 'default'}>
            {stageLabel(r.execution_stage)}
          </Tag>
        ) : (
          <Tag>Not opened</Tag>
        ),
    },
    {
      title: 'Hold',
      key: 'hold',
      width: 160,
      render: (_, r) =>
        r.hold_reason ? <Tag color={HOLD_TAG_COLOR}>{holdLabel(r.hold_reason)}</Tag> : '—',
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      width: 90,
      render: (p: string) => (
        <Tag color={p === 'urgent' ? 'red' : p === 'high' ? 'orange' : 'blue'}>{p}</Tag>
      ),
    },
    {
      title: 'Next action',
      key: 'next',
      ellipsis: true,
      render: (_, r) => <Text>{nextActionForJob(r)}</Text>,
    },
    {
      title: '',
      key: 'go',
      width: 100,
      render: (_, r) => (
        <Button type="link" onClick={() => navigate(`/asset/workshop/jobs/${r.case_id}`)}>
          Open
        </Button>
      ),
    },
  ];

  return (
    <WorkspaceLayout>
      <EnterprisePageHeader
        documentLabel="Workshop Operations"
        documentNumber="Garage & site jobs"
        subtitle="Intake, holds, parts, labour, QC and release — Maintenance Work Order remains MNT-…"
      />

      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Unable to load workshop jobs"
          description={(error as Error)?.message || 'Please retry.'}
          action={
            <Button size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic title="Open cases" value={summary.total} prefix={<ToolOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic title="Active jobs" value={summary.active} prefix={<ClockCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic
              title="On hold"
              value={summary.onHold}
              valueStyle={{ color: summary.onHold ? '#d46b08' : undefined }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic title="QC pending" value={summary.qcPending} prefix={<SafetyCertificateOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic
              title="Ready for release"
              value={summary.ready}
              valueStyle={{ color: summary.ready ? '#389e0d' : undefined }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic
              title="Vendor-blocked"
              value={summary.vendorBlocked}
              valueStyle={{ color: summary.vendorBlocked ? '#cf1322' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      {attention.length > 0 && (
        <Card
          title="Needs attention"
          size="small"
          style={{ marginBottom: 16 }}
          styles={{ body: { paddingTop: 8 } }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            {attention.map((row) => {
              const kind: AttentionKind =
                row.cost_responsibility === 'VENDOR' && !row.execution_id
                  ? 'vendor'
                  : row.execution_stage === 'READY_FOR_RELEASE'
                    ? 'release'
                    : row.execution_stage === 'QC_PENDING'
                      ? 'qc'
                      : row.hold_reason
                        ? 'hold'
                        : 'no_execution';
              return (
                <Alert
                  key={row.case_id}
                  type={
                    kind === 'vendor'
                      ? 'error'
                      : kind === 'release'
                        ? 'success'
                        : kind === 'qc'
                          ? 'warning'
                          : 'info'
                  }
                  showIcon
                  message={
                    <Space wrap>
                      <Text strong>
                        {row.asset_code || 'Equipment'} · {row.case_number}
                      </Text>
                      {row.execution_stage && (
                        <Tag color={STAGE_TAG_COLOR[row.execution_stage]}>
                          {stageLabel(row.execution_stage)}
                        </Tag>
                      )}
                      {row.hold_reason && (
                        <Tag color={HOLD_TAG_COLOR}>{holdLabel(row.hold_reason)}</Tag>
                      )}
                      {kind === 'vendor' && <Tag color="red">Vendor maintenance responsibility</Tag>}
                    </Space>
                  }
                  description={nextActionForJob(row)}
                  action={
                    <Button size="small" type="primary" onClick={() => navigate(`/asset/workshop/jobs/${row.case_id}`)}>
                      Open job
                    </Button>
                  }
                />
              );
            })}
          </Space>
        </Card>
      )}

      <Card
        title="Active work"
        extra={
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="Search equipment or MNT…"
              style={{ width: 220 }}
              onSearch={setQ}
              onChange={(e) => !e.target.value && setQ('')}
            />
            <Select
              allowClear
              placeholder="Stage"
              style={{ width: 160 }}
              value={stageFilter}
              onChange={setStageFilter}
              options={Object.entries(
                Object.fromEntries(
                  [
                    'QUEUED',
                    'RECEIVED',
                    'IN_REPAIR',
                    'QC_PENDING',
                    'READY_FOR_RELEASE',
                  ].map((s) => [s, stageLabel(s)])
                )
              ).map(([value, label]) => ({ value, label }))}
            />
            <Select
              allowClear
              placeholder="Hold"
              style={{ width: 180 }}
              value={holdFilter}
              onChange={setHoldFilter}
              options={[
                { value: 'WAITING_PARTS', label: holdLabel('WAITING_PARTS')! },
                { value: 'WAITING_TECHNICIAN', label: holdLabel('WAITING_TECHNICIAN')! },
                { value: 'WAITING_VENDOR', label: holdLabel('WAITING_VENDOR')! },
                { value: 'WAITING_APPROVAL', label: holdLabel('WAITING_APPROVAL')! },
              ]}
            />
          </Space>
        }
      >
        <Table
          rowKey="case_id"
          loading={isLoading}
          columns={columns}
          dataSource={filtered}
          pagination={{ pageSize: 15, showSizeChanger: true }}
          scroll={{ x: 900 }}
          locale={{
            emptyText: (
              <Empty description="No open maintenance cases for the workshop queue. Escalate a breakdown or open a maintenance case first." />
            ),
          }}
          onRow={(r) => ({
            onClick: () => navigate(`/asset/workshop/jobs/${r.case_id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>
    </WorkspaceLayout>
  );
};

export default WorkshopOperationsPage;
